/**
 * CRM Tab - Sales & Customer Relationship Management
 * Shows deal pipeline, contacts, and sales metrics for businesses with CRM module enabled
 */
import React, { useState, useCallback, useEffect } from 'react';
import { toast } from '@/utils/safeToast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Target, Users, DollarSign, TrendingUp, Phone, Mail, Calendar,
  ChevronRight, Plus, Filter, BarChart3, Clock, CheckCircle2, AlertCircle,
  ArrowUpRight, ArrowDownRight, Briefcase, UserPlus, MessageSquare
} from 'lucide-react';
import { formatCurrency } from '@/lib/formatters';

// Demo deal data generator
const generateDealData = (industry) => {
  const stages = ['Lead', 'Qualified', 'Proposal', 'Negotiation', 'Closed Won'];
  const stageColors = {
    'Lead': 'bg-slate-100 text-slate-700',
    'Qualified': 'bg-blue-100 text-blue-700',
    'Proposal': 'bg-amber-100 text-amber-700',
    'Negotiation': 'bg-purple-100 text-purple-700',
    'Closed Won': 'bg-emerald-100 text-emerald-700'
  };
  
  const industryDeals = {
    hospitality: ['New Venue Contract', 'Catering Service', 'Corporate Event', 'Wedding Package'],
    healthcare: ['Clinic Partnership', 'Equipment Lease', 'Service Agreement', 'Insurance Contract'],
    retail: ['Wholesale Order', 'Franchise Expansion', 'Corporate Gift Order', 'Bulk Purchase'],
    wholesale: ['Distribution Deal', 'Supplier Agreement', 'Volume Contract', 'Retailer Partnership'],
    professional: ['Consulting Engagement', 'Annual Retainer', 'Project Scope', 'Advisory Contract'],
    default: ['New Client', 'Service Upgrade', 'Contract Renewal', 'Partnership Deal']
  };
  
  const dealNames = industryDeals[industry] || industryDeals.default;
  
  return stages.map((stage, idx) => ({
    stage,
    color: stageColors[stage],
    count: Math.floor(Math.random() * 8) + 2,
    value: Math.floor(Math.random() * 150000) + 20000,
    deals: Array(Math.floor(Math.random() * 4) + 1).fill(0).map((_, i) => ({
      id: `deal-${idx}-${i}`,
      name: dealNames[Math.floor(Math.random() * dealNames.length)],
      company: `${['Acme', 'Global', 'Prime', 'Elite', 'Pacific'][Math.floor(Math.random() * 5)]} ${['Corp', 'Ltd', 'Group', 'Solutions', 'Partners'][Math.floor(Math.random() * 5)]}`,
      value: Math.floor(Math.random() * 50000) + 5000,
      probability: stage === 'Closed Won' ? 100 : 20 + (idx * 15) + Math.floor(Math.random() * 10),
      daysInStage: Math.floor(Math.random() * 14) + 1,
      owner: ['Sarah', 'Mike', 'Emma', 'James'][Math.floor(Math.random() * 4)],
      nextAction: ['Follow up call', 'Send proposal', 'Schedule meeting', 'Contract review'][Math.floor(Math.random() * 4)]
    }))
  }));
};

// Generate recent activities
const generateActivities = () => [
  { type: 'call', contact: 'John Smith', company: 'Acme Corp', time: '2 hours ago', result: 'positive' },
  { type: 'email', contact: 'Sarah Johnson', company: 'Pacific Ltd', time: '4 hours ago', result: 'pending' },
  { type: 'meeting', contact: 'Mike Wilson', company: 'Elite Group', time: 'Yesterday', result: 'positive' },
  { type: 'call', contact: 'Emma Brown', company: 'Prime Solutions', time: 'Yesterday', result: 'neutral' },
  { type: 'email', contact: 'James Davis', company: 'Global Partners', time: '2 days ago', result: 'positive' },
];

const CRMTab = ({ businessName, industry, userName }) => {
  const [dealData, setDealData] = useState([]);
  const [activities] = useState(generateActivities());
  const [selectedStage, setSelectedStage] = useState(null);
  
  useEffect(() => {
    setDealData(generateDealData(industry));
  }, [industry]);
  
  // Calculate totals
  const totalPipeline = dealData.reduce((sum, stage) => sum + stage.value, 0);
  const totalDeals = dealData.reduce((sum, stage) => sum + stage.count, 0);
  const closedWonValue = dealData.find(s => s.stage === 'Closed Won')?.value || 0;
  const avgDealSize = totalDeals > 0 ? totalPipeline / totalDeals : 0;
  
  // Win rate calculation
  const closedDeals = dealData.find(s => s.stage === 'Closed Won')?.count || 0;
  const qualifiedDeals = dealData.slice(1).reduce((sum, stage) => sum + stage.count, 0);
  const winRate = qualifiedDeals > 0 ? Math.round((closedDeals / qualifiedDeals) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* CRM Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Sales Pipeline</h2>
          <p className="text-sm text-slate-500">
            Track deals and manage your sales process for {businessName || 'your business'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <Filter className="w-4 h-4" />
            Filter
          </Button>
          <Button size="sm" className="gap-2 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4" />
            Add Deal
          </Button>
        </div>
      </div>
      
      {/* Key Metrics */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pipeline Value</p>
                <p className="text-lg font-bold text-slate-900">{formatCurrency(totalPipeline)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Closed This Month</p>
                <p className="text-lg font-bold text-slate-900">{formatCurrency(closedWonValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Active Deals</p>
                <p className="text-lg font-bold text-slate-900">{totalDeals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Win Rate</p>
                <p className="text-lg font-bold text-slate-900">{winRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Pipeline Visualization */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-indigo-600" />
            Deal Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Pipeline Stages */}
          <div className="flex gap-2 overflow-x-auto pb-4">
            {dealData.map((stage, idx) => (
              <div
                key={stage.stage}
                onClick={() => setSelectedStage(selectedStage === idx ? null : idx)}
                className={`flex-1 min-w-[160px] p-4 rounded-xl cursor-pointer transition-all ${
                  selectedStage === idx 
                    ? 'bg-indigo-50 ring-2 ring-indigo-500' 
                    : 'bg-slate-50 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge className={`${stage.color} text-xs`}>{stage.stage}</Badge>
                  <span className="text-xs text-slate-500">{stage.count} deals</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(stage.value)}</p>
                <Progress 
                  value={(stage.value / totalPipeline) * 100} 
                  className="h-1.5 mt-2" 
                />
              </div>
            ))}
          </div>
          
          {/* Selected Stage Deals */}
          {selectedStage !== null && dealData[selectedStage] && (
            <div className="mt-4 pt-4 border-t">
              <h4 className="text-sm font-medium text-slate-900 mb-3">
                Deals in {dealData[selectedStage].stage}
              </h4>
              <div className="space-y-2">
                {dealData[selectedStage].deals.map(deal => (
                  <div 
                    key={deal.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{deal.name}</p>
                        <p className="text-xs text-slate-500">{deal.company}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">{formatCurrency(deal.value)}</p>
                      <p className="text-xs text-slate-500">{deal.probability}% probability</p>
                    </div>
                    <div className="hidden sm:block text-right">
                      <p className="text-xs text-slate-500">Owner: {deal.owner}</p>
                      <p className="text-xs text-amber-600">{deal.nextAction}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Bottom Row */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-indigo-600" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activities.map((activity, idx) => (
                <div key={`${activity?.id ?? activity?.name ?? activity?.title ?? 'activity'}-${idx}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    activity.type === 'call' ? 'bg-blue-100' :
                    activity.type === 'email' ? 'bg-purple-100' : 'bg-emerald-100'
                  }`}>
                    {activity.type === 'call' && <Phone className="w-4 h-4 text-blue-600" />}
                    {activity.type === 'email' && <Mail className="w-4 h-4 text-purple-600" />}
                    {activity.type === 'meeting' && <Calendar className="w-4 h-4 text-emerald-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{activity.contact}</p>
                    <p className="text-xs text-slate-500">{activity.company} • {activity.time}</p>
                  </div>
                  {activity.result === 'positive' && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                  {activity.result === 'pending' && <Clock className="w-4 h-4 text-amber-500" />}
                  {activity.result === 'neutral' && <AlertCircle className="w-4 h-4 text-slate-400" />}
                </div>
              ))}
            </div>
            <Button variant="ghost" size="sm" className="w-full mt-3 text-indigo-600" onClick={() => toast.info('Loading the full activity timeline...')}>
              View All Activity
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
        
        {/* Quick Actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="w-4 h-4 text-indigo-600" />
              Sales Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Button variant="outline" className="h-auto py-4 flex-col gap-2">
                <UserPlus className="w-5 h-5 text-indigo-600" />
                <span className="text-xs">Add Contact</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2">
                <Briefcase className="w-5 h-5 text-emerald-600" />
                <span className="text-xs">New Deal</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2">
                <Phone className="w-5 h-5 text-blue-600" />
                <span className="text-xs">Log Call</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                <span className="text-xs">Send Email</span>
              </Button>
            </div>
            
            {/* Today's Focus */}
            <div className="mt-4 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg">
              <h4 className="text-sm font-medium text-slate-900 mb-2">Today&apos;s Focus</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  <span>3 follow-up calls scheduled</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <AlertCircle className="w-3 h-3 text-amber-500" />
                  <span>2 proposals awaiting response</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <ArrowUpRight className="w-3 h-3 text-indigo-500" />
                  <span>$45,000 in deals closing this week</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CRMTab;
